/**
 * Created by Ivo Mehide <ivo@degeetia.ee> on 03.06.17.
 */

import _ from "underscore";
import to from "to-case";
import t from "./trans";

class UiSchema {
    /**
     * Creates new UiSchema object
     * @public
     * @param {JsonUiSchema} uiSchema UI Schema corresponding to this object
     * @param {String} fieldName Field name corresponding to this object
     */
    constructor(uiSchema, fieldName) {
        /**
         * @type {JsonUiSchema}
         */
        this.uiSchema = (uiSchema ? uiSchema : {});
        this.fieldName = fieldName;
        this.fieldCache = {};
    }

    getFieldName() {
        return this.fieldName;
    }

    isEmpty() {
        _.isEmpty(this.uiSchema);
    }

    /**
     * @public
     * @returns {JsonUiSchema}
     */
    getUiSchema() {
        return this.uiSchema;
    }

    hasTitle() {
        return this.getSchemaElement("title") != null;
    }
    /**
     * Returns title
     * @public
     * @returns {String} Title
     */
    getTitle() {
        // t("ui:title")
        let title = t(this.getSchemaElement("title"));
        if (title) return title;
        if (this.fieldName) return t(to.title(this.fieldName));
        return null;
    }

    /**
     * Returns title in plural
     * @public
     * @returns {String} Title in plural
     */
    getTitlePl() {
        // t("ui:titlePl")
        let title = t(this.getSchemaElement("titlePl"));
        if (title) return title;
        return t(to.title(this.fieldName));
    }

    getTitleField() {
        return this.getSchemaElement("titleField")
    }

    getController() {
        return this.getSchemaElement("controller");
    }

    getPage() {
        return this.getSchemaElement("page");
    }

    getDataHandler() {
        return this.getSchemaElement("dataHandler");
    }

    getResource() {
        return this.getSchemaElement("resource");
    }

    getFilter() {
        return this.getSchemaElement("filter");
    }

    getSearch() {
        return this.getSchemaElement("search");
    }

    getSort() {
        return this.getSchemaElement("sort");
    }

    getOptions() {
        return this.getSchemaElement("options");
    }

    getWidget() {
        return this.getSchemaElement("widget");
    }

    getAction() {
        let action = this.getSchemaElement("action");
        if (!action || _.isArray(action)) return action;
        return [action];
    }

    getAccess() {
        return this.getSchemaElement("access");
    }

    getOp() {
        return this.getSchemaElement("op");
    }

    getDefaultFilter() {
        return this.getSchemaElement("defaultFilter");
    }

    /**
     * Returns true if current schema contains provided simple field
     * @param {String} field Simple field name
     */
    hasField(field) {
        return _.has(this.uiSchema, field);
    }

    /**
     * Returns Schema object corresponding to the specified field
     * @public
     * @param {string|number|string[]} field Simple field name, record number, hierarchical field name or herarchical field names array
     * @returns {UiSchema}
     */
    field(field) {
        if (field == null || field === "") return this;

        let fieldName = field;
        if (_.isArray(field)) fieldName = _.reduce(field, function (memo, val) {
            return memo.toString() + "/" + val.toString();
        });
        if (this.fieldCache[fieldName]) return this.fieldCache[fieldName];

        let currentUiSchema = this.uiSchema;

        if (_.has(currentUiSchema, fieldName)) {
            currentUiSchema = currentUiSchema[fieldName];
        } else {
            let fieldParts = [];
            if (_.isArray(field)) fieldParts = field;
            else fieldParts = field.toString().split("/");

            for (let f of fieldParts) {
                if (!currentUiSchema) break;
                if (_.isArray(currentUiSchema)) {
                    // Field is array index:
                    let index = parseInt(f);
                    if (isNaN(index)) index = 0;
                    currentUiSchema = currentUiSchema[index];
                } else if (_.has(currentUiSchema, f)) currentUiSchema = currentUiSchema[f];
                else if (_.has(currentUiSchema, "*")) currentUiSchema = currentUiSchema["*"];
                else currentUiSchema = null;
            }
        }

        let fieldSchema = new UiSchema(currentUiSchema, field);
        this.fieldCache[fieldName] = fieldSchema;
        return fieldSchema;
    }

    getSchemaElement(element) {
        if (this.uiSchema && this.uiSchema["ui:" + element]) return this.uiSchema["ui:" + element];
        return null;
    }
}

export default UiSchema;

/**
 * @typedef {Object} JsonUiSchema
 * @property {String[]} ui:order List of fields
 * @property {String} ui:field
 * @property {String} ui:title Field title
 * @property {String|String[]} ui:titleField Fields used for creating value of the title
 * @property {String} ui:template Template used for displaying this element
 * @property {String} ui:widget Widget for displaying this element's value
 * @property {JsonUiSchemaOptions} ui:options Additional options passed to the widget
 * @property {String[]} ui:valueFormatter Component and parmeters used for formatting element value
 * @property {JsonUiSchema} * Field JSON UI Schema
 */

/**
 * @typedef {Object} JsonUiSchemaOptions
 * @property {boolean} searchable Use only for select widget. If true then widget displays search area for searching the value.
 * @property {Object} checkedValue Used only for checkbox widget. Specifies value for checked state.
 * @property {Object} uncheckedValue Used only for checkbox widget. Specifies value for unchecked state.
 * @property {String|String[]} linkUrl Used only for link widget. Specifies link for value or sprintf format string and field names used for making URL.
 */
